import { useCallback, useState, useEffect } from 'react'
import { useQueryClient } from 'react-query'
import { useHistory, useLocation, generatePath } from 'react-router-dom'

import Styles from './AutopilotPlansModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import client from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'

import { useRootStore } from 'stores/RootStore'
import { AutopilotPlan, StripePrice } from '../VirtualMarketerUpgradeCta/types'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { UpgradePlanModal } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { Form } from './components/Form/Form'
import { Plans } from './components/Plans/Plans'
import { Cta } from './components/Cta/Cta'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CampaignQuestions, CampaignQuestionsData } from './components/CampaignQuestions/CampaignQuestions'
import { Channels } from './components/Channels/Channels'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useUpgradeBrandPlan } from 'hooks/useUpgradeBrandPlan'
import { useUpdateAutopilotSubscription } from 'hooks/useUpdateAutopilotSubscription'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { BrandPlanTacticsNextData } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/Types'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { FetchSubscriptionResponse } from 'api/clients/WorkspaceClient'
import { DowngradeConfirmation } from './components/DowngradeConfirmation/DowngradeConfirmation'
import { LoseMembers } from './components/LoseMembers/LoseMembers'
import { SettingsMember, SettingsGuest, BrandPlanStrategyType, Workspace, BrandPlan } from 'types'
import { useAuth } from 'hooks/UseAuth'
import { AutopilotFreemiumChannelSelectModal } from 'components/AutopilotFreemiumChannelSelectModal/AutopilotFreemiumChannelSelectModal'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { FormValues } from 'components/AutopilotFreemiumChannelSelectModal/types'
import { buildLimitedChannels } from 'components/AutopilotFreemiumChannelSelectModal/utils'
import { useDowngradeBrandPlan } from 'hooks/useDowngradeBrandPlan'
import {
  nameMap,
  planOrder,
} from 'components/Settings/Workspace/AutopilotPlans/components/Plans/components/PlanDetails/PlanDetails'
import { useFetchAllBrandPlans } from 'hooks/queries/brandPlans/useFetchAllBrandPlans'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { BLAZE_HOME_PATH, CAMPAIGNS_PATH } from 'paths'
import { CampaignGenerationLoadingScreen } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignGenerationLoadingScreen'
import { CampaignGenerationLoadingProvider } from 'components/FirstTimeExperienceModal/steps/CampaignGeneration/CampaignGenerationLoadingContext'
import { useUpdateAutoReloadCredits } from 'hooks/useUpdateAutoReloadCredits'
import { useOnboardingStrategiesEnabled } from 'flows/virtualMarketerOnboarding/steps'

// Channels now passes BrandPlanTacticsNextData directly, so we don't need HandleSubmitParams anymore

const UpgradeLoadingScreen = ({
  brandPlanId,
  campaignData,
  onComplete,
  onError,
}: {
  brandPlanId: number
  campaignData?: { growth_goals_input: string; key_dates_input: string }
  onComplete: () => void
  onError: () => void
}) => (
  <CampaignGenerationLoadingProvider>
    <CampaignGenerationLoadingScreen
      brandPlanId={brandPlanId}
      campaignData={campaignData}
      startDate={undefined}
      onComplete={onComplete}
      onBack={onError}
    />
  </CampaignGenerationLoadingProvider>
)

type Step =
  | 'cta'
  | 'plans'
  | 'selected'
  | 'checkout'
  | 'campaign_questions'
  | 'channels'
  | 'upgradeLoading'
  | 'downgrade_confirmation'
  | 'lose_members'

const STEPS: Step[] = ['cta', 'plans', 'selected', 'campaign_questions', 'channels']

export type OnStartPlan = ({
  paymentMethodId,
  chosenPriceId,
  selectedPlanOverride,
  promotionCode,
}: {
  paymentMethodId?: string
  chosenPriceId: string
  selectedPlanOverride?: AutopilotPlan
  promotionCode?: string
}) => void

const determineIsUpgrade = (
  currentSubscription: FetchSubscriptionResponse | null,
  selectedPlan: AutopilotPlan,
  workspace?: { is_ap_freemium?: boolean } | null,
): boolean => {
  if (!currentSubscription || !selectedPlan) return false

  if (workspace?.is_ap_freemium) {
    return selectedPlan.key === PaymentPlanKey.BlazeAutopilotGrowth
  }

  const currentPlanKey = currentSubscription.plan

  const isUpgradeFromPlan =
    currentPlanKey === PaymentPlanKey.VirtualMarketer || currentPlanKey === PaymentPlanKey.BlazeAutopilotStarter
  const isUpgradeToPlan = selectedPlan.key === PaymentPlanKey.BlazeAutopilotGrowth

  return isUpgradeFromPlan && isUpgradeToPlan
}

const determineIsDowngrade = (
  currentSubscription: FetchSubscriptionResponse | null,
  selectedPlan: AutopilotPlan,
): boolean => {
  if (!currentSubscription || !selectedPlan) return false

  const currentPlanKey = currentSubscription.plan

  const isDowngradeFromPlan =
    currentPlanKey === PaymentPlanKey.BlazeAutopilotGrowth ||
    currentPlanKey === PaymentPlanKey.BlazeAutopilotEnterprise ||
    currentPlanKey === PaymentPlanKey.VirtualMarketer
  const isDowngradeToPlan =
    selectedPlan.key === PaymentPlanKey.BlazeAutopilotStarter || selectedPlan.key === PaymentPlanKey.Free

  return isDowngradeFromPlan && isDowngradeToPlan
}

export const AutopilotPlansModal = ({
  initialStep = 'plans',
  initialPlanAndPrice,
  skipRedirect = false,
  skipCloseAllModals = false,
  reactivate = false,
}: {
  initialStep?: Step
  initialPlanAndPrice?: { plan: AutopilotPlan; price?: StripePrice }
  skipRedirect?: boolean
  skipCloseAllModals?: boolean
  reactivate?: boolean
}) => {
  const auth = useAuth()
  const workspace = useCurrentWorkspace()
  const [step, setStep] = useState<Step>(initialStep)
  const [selectedPlan, setSelectedPlan] = useState<AutopilotPlan | null>(initialPlanAndPrice?.plan || null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [shouldAutoReloadCredits, setShouldAutoReloadCredits] = useState(true)
  const { openModal, closeModal, closeAllModals } = useModals()
  const { tierUsageStore, creditUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const [paymentMethodId, setPaymentMethodId] = useState<string | undefined>(undefined)
  const [promotionCode, setPromotionCode] = useState<string | undefined>(undefined)
  const [chosenPriceId, setChosenPriceId] = useState<string | null>(initialPlanAndPrice?.price?.id || null)
  const [campaignQuestions, setCampaignQuestions] = useState<CampaignQuestionsData>({ goals: '', keyDates: '' })
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: brandProfile } = useBrandProfile()
  const { data: externalIntegrationsData } = useExternalIntegrations()
  const { mutate: upgradeBrandPlan, isLoading: isUpgradingBrandPlan } = useUpgradeBrandPlan()
  const { mutate: downgradeBrandPlan, isLoading: isDowngradingBrandPlan } = useDowngradeBrandPlan()
  const updateAutoReloadCredits = useUpdateAutoReloadCredits()
  const queryClient = useQueryClient()
  const [allMembers, setAllMembers] = useState<(SettingsMember | SettingsGuest)[]>([])
  const [savedChannelcounts, setSavedChannelCounts] = useState<FormValues | undefined>()
  const history = useHistory()
  const location = useLocation()
  const { data: brandPlans } = useFetchAllBrandPlans()
  const isTrialing = currentSubscription?.status === 'trialing'
  const isActive = currentSubscription?.status === 'active'
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const isOnboardingStrategiesEnabled = useOnboardingStrategiesEnabled() && brandPlan?.content_strategies !== null

  const removeSettingsModalQueryParam = useCallback(() => {
    const queryParams = new URLSearchParams(location.search)
    queryParams.delete('settings')
    setTimeout(() => {
      history.push({ search: queryParams.toString() })
    }, 250)
  }, [history, location.search])

  const { updateSubscription, isUpdating } = useUpdateAutopilotSubscription({
    closeAllModals: skipCloseAllModals ? closeModal : closeAllModals,
    removeSettingsModalQueryParam: skipCloseAllModals ? undefined : removeSettingsModalQueryParam,
    skipRedirect,
  })

  const openChannelSelectModal = (plan: PaymentPlanKey, priceId?: string, paymentMethodIdOverride?: string) => {
    if (isOnboardingStrategiesEnabled) {
      if (priceId) {
        setChosenPriceId(priceId)
      }

      if (allMembers.length === 0) {
        handleDowngradePlanForStrategies(priceId, paymentMethodIdOverride)
      } else {
        setStep('lose_members')
      }

      return
    }

    openModal(AutopilotFreemiumChannelSelectModal, {
      onSubmit: (values) => {
        setSavedChannelCounts(values)
        // Set the price ID if it was provided
        if (priceId) {
          setChosenPriceId(priceId)
        }
        if (allMembers.length === 0) {
          handleDowngradePlan(values, priceId, paymentMethodIdOverride)
        } else {
          setStep('lose_members')
          closeModal()
        }
      },
      forcePlan: plan,
      savedValues: savedChannelcounts,
    })
  }

  const loadAllMembers = useCallback(() => {
    if (!auth) return

    const load = async () => {
      const [membersResp, guestsResp] = await Promise.all([
        client.getWorkspaceMembers(auth.company_key),
        client.getWorkspaceGuests(auth.company_key),
      ])

      const members: SettingsMember[] = []
      const guests: SettingsGuest[] = []

      if (membersResp.type === SUCCESS_RESPONSE_TYPE) {
        members.push(...membersResp.data.data.members)
      }

      if (guestsResp.type === SUCCESS_RESPONSE_TYPE) {
        guests.push(...guestsResp.data.guests)
      }

      // Combine all members and guests into a single array
      const filteredMembers = [...members, ...guests].filter(
        (member) => member.id !== auth.id && member.type !== 'DeletedMembership',
      )
      setAllMembers(filteredMembers)
    }

    load()
  }, [auth])

  useEffect(() => {
    loadAllMembers()
  }, [loadAllMembers])

  const canAutoReloadCredits = shouldAutoReloadCredits

  const handleBack = () => {
    if (step === initialStep) {
      closeModal()
      return
    }

    if (step === 'checkout') {
      return setStep('selected')
    }

    if (selectedPlan && step === 'selected') {
      setSelectedPlan(null)
      return setStep('plans')
    }

    if (selectedPlan && step === 'lose_members') {
      openChannelSelectModal(selectedPlan.key, selectedPlan.prices?.[0]?.id)
      return setTimeout(() => setStep('downgrade_confirmation'), 500)
    }

    if (step === 'downgrade_confirmation') {
      setSelectedPlan(null)
      return setStep('plans')
    }

    setStep(STEPS[Math.max(STEPS.indexOf(step) - 1, 0)])
  }

  const savePaymentInformation: OnStartPlan = async ({
    paymentMethodId,
    chosenPriceId,
    selectedPlanOverride,
    promotionCode: promoCode,
  }) => {
    setPaymentMethodId(paymentMethodId)
    setChosenPriceId(chosenPriceId)
    setPromotionCode(promoCode)

    const selected = selectedPlanOverride || selectedPlan
    if (isOnPioneer) {
      if (selected?.key === PaymentPlanKey.BlazeAutopilotGrowth) {
        if (isOnboardingStrategiesEnabled) {
          handleUpgradeBrandPlanForStrategies(chosenPriceId, paymentMethodId)
        } else {
          setStep('campaign_questions')
        }

        return
      }

      if (selected?.key === PaymentPlanKey.Free) {
        return
      }
    }

    const isUpgrade = selected ? determineIsUpgrade(currentSubscription, selected, workspace) : false
    const isDowngrade = selected ? determineIsDowngrade(currentSubscription, selected) : false
    const hasExistingCampaigns =
      isUpgrade && !!brandPlans?.find((bp) => !bp.active && bp.strategy_type === BrandPlanStrategyType.Crosspost)

    if (selectedPlan?.key === PaymentPlanKey.BlazeAutopilotStarter && workspace?.is_ap_freemium) {
      return updateSubscription(
        {
          paymentMethodId,
          priceId: chosenPriceId,
          reactivate,
          promotionCode: promoCode,
        },
        {
          onSuccess: () => {
            const autoReloadLimit = canAutoReloadCredits ? 500 : null
            updateAutoReloadCredits.mutate({ autoReloadCredits: canAutoReloadCredits, autoReloadLimit })
          },
        },
      )
    }

    if (isUpgrade && !hasExistingCampaigns) {
      if (isOnboardingStrategiesEnabled) {
        return handleUpgradeBrandPlanForStrategies(chosenPriceId, paymentMethodId)
      } else {
        return setStep('campaign_questions')
      }
    }

    if (isUpgrade && hasExistingCampaigns) {
      // For existing campaigns, we still need to pass minimal data
      return handleUpgradeBrandPlan(
        {
          tactics: brandPlan?.tactics || [],
          seoKeywords: brandPlan?.brand_profile?.keywords || [],
          idealCustomers: brandPlan?.brand_profile?.ideal_customer_profiles || [],
        },
        chosenPriceId,
        paymentMethodId,
      )
    }

    if (isDowngrade && selected) {
      return openChannelSelectModal(selected.key, chosenPriceId, paymentMethodId)
    }

    // We pass the overrides to avoid the state not being updated in time
    updateSubscription(
      {
        paymentMethodId,
        priceId: chosenPriceId,
        reactivate,
        promotionCode: promoCode,
      },
      {
        onSuccess: () => {
          const autoReloadLimit = canAutoReloadCredits ? 500 : null
          updateAutoReloadCredits.mutate({ autoReloadCredits: canAutoReloadCredits, autoReloadLimit })
        },
      },
    )
  }

  const handleCancelSubscription = () => {
    closeModal()
    openModal(
      UpgradePlanModal,
      {
        initialPage: 'provideReason',
        onClose: closeModal,
        showSuccessModal: false,
      },
      { newStack: true },
    )
  }

  const onUpgradeBrandPlanSuccess = async (brandPlan: BrandPlan) => {
    const autoReloadLimit = canAutoReloadCredits ? 500 : null
    updateAutoReloadCredits.mutate({ autoReloadCredits: canAutoReloadCredits, autoReloadLimit })
    // Update the useCurrentBrandPlan query cache with the return brandPlan
    if (workspace) {
      await Promise.all([
        tierUsageStore.refetch(workspace.id),
        creditUsageStore.fetch(workspace.id),
        WorkspaceService.getWorkspaces(),
      ])
      queryClient.setQueryData(['workspaces', workspace.id, 'brandPlans', 'current'], brandPlan)
    } else {
      await WorkspaceService.getWorkspaces()
    }
  }

  const handleUpgradeBrandPlanForStrategies = async (
    overrideChosenPriceId?: string,
    overridePaymentMethodId?: string,
  ) => {
    const priceId = overrideChosenPriceId || chosenPriceId
    const effectivePaymentMethodId = overridePaymentMethodId !== undefined ? overridePaymentMethodId : paymentMethodId

    if (!priceId) return

    const dismiss = showToastMessage('Processing plan change...', 'loading', undefined, 0)
    setIsProcessing(true)

    upgradeBrandPlan(
      {
        payment_method_id: effectivePaymentMethodId,
        price_id: priceId,
        promotion_code: promotionCode,
      },
      {
        onSuccess: async (brandPlan) => {
          await onUpgradeBrandPlanSuccess(brandPlan)

          dismiss()
          setIsProcessing(false)
          showToastMessage('Upgraded to Growth plan successfully!')
          closeAllModals()
          removeSettingsModalQueryParam()
          queryClient.invalidateQueries(['can_create_additional_workspace'])
        },
        onError: () => {
          dismiss()
          setIsProcessing(false)
          showToastMessage(
            'An error occurred during upgrade. Try again. Contact support if the issue persists.',
            'error',
          )
        },
      },
    )
  }

  const handleUpgradeBrandPlan = async (
    values: BrandPlanTacticsNextData,
    overrideChosenPriceId?: string,
    overridePaymentMethodId?: string,
  ) => {
    const priceId = overrideChosenPriceId || chosenPriceId
    const effectivePaymentMethodId = overridePaymentMethodId !== undefined ? overridePaymentMethodId : paymentMethodId

    if (!priceId) return

    const dismiss = showToastMessage('Processing plan change...', 'loading', undefined, 0)
    setIsProcessing(true)

    const growthGoalsInput = campaignQuestions.growth_goals_input || campaignQuestions.goals
    const keyDatesInput = campaignQuestions.key_dates_input || campaignQuestions.keyDates

    upgradeBrandPlan(
      {
        payment_method_id: effectivePaymentMethodId,
        price_id: priceId,
        promotion_code: promotionCode,
        growth_goals_input: growthGoalsInput,
        key_dates_input: keyDatesInput,
        strategy: { type: 'CrosspostStrategy' },
        start_date: values.startDate,
        tactics: values.tactics,
        crosspost_frequency: values.crosspost_frequency,
        ...(values.crosspost_contents && { crosspost_contents: values.crosspost_contents }),
        ...(values.video_crosspost_contents && { video_crosspost_contents: values.video_crosspost_contents }),
        ...(values.instagram_contents && { instagram_contents: values.instagram_contents }),
        ...(values.facebook_contents && { facebook_contents: values.facebook_contents }),
        ...(values.linkedin_contents && { linkedin_contents: values.linkedin_contents }),
        ...(values.twitter_contents && { twitter_contents: values.twitter_contents }),
        ...(values.tiktok_contents && { tiktok_contents: values.tiktok_contents }),
        ...(values.youtube_contents && { youtube_contents: values.youtube_contents }),
        ...(values.seo_contents && { seo_contents: values.seo_contents }),
        ...(values.email_contents && { email_contents: values.email_contents }),
        ...(values.selectedBlogPlatform && { selected_blog_platform: values.selectedBlogPlatform }),
        ...(values.selectedEmailPlatform && { selected_email_platform: values.selectedEmailPlatform }),
      },
      {
        onSuccess: async (brandPlan) => {
          onUpgradeBrandPlanSuccess(brandPlan)
          dismiss()
          setIsProcessing(false)
          setStep('upgradeLoading')
        },
        onError: () => {
          dismiss()
          setIsProcessing(false)
          showToastMessage(
            'An error occurred during upgrade. Try again. Contact support if the issue persists.',
            'error',
          )
        },
      },
    )
  }

  const onDowngradeBrandPlanSuccess = useCallback(
    async (brandPlan: BrandPlan, effectivePriceId: string | null, dismiss: () => void) => {
      const autoReloadLimit = canAutoReloadCredits ? 500 : null
      updateAutoReloadCredits.mutate({ autoReloadCredits: canAutoReloadCredits, autoReloadLimit })
      // Update the useCurrentBrandPlan query cache with the return brandPlan
      if (workspace) {
        await Promise.all([tierUsageStore.refetch(workspace.id), creditUsageStore.fetch(workspace.id)])
        queryClient.setQueryData(['workspaces', workspace.id, 'brandPlans', 'current'], brandPlan)
        queryClient.refetchQueries(['workspaces', workspace.id, 'brandPlans'])
      }
      if (!effectivePriceId) {
        await WorkspaceService.getWorkspaces()
      }
      dismiss()
      setIsProcessing(false)
      if (selectedPlan?.key === PaymentPlanKey.Free && isActive) {
        showToastMessage(
          'Plan Changed successfully! You will be converted to the free plan once your current plan ends.',
        )
      } else {
        showToastMessage('Plan changed successfully!')
      }
      closeAllModals()
      removeSettingsModalQueryParam()
      queryClient.invalidateQueries(['can_create_additional_workspace'])

      if (workspace && !isOnboardingStrategiesEnabled) {
        history.push(generatePath(BLAZE_HOME_PATH, { workspaceId: workspace.id }))
      }
    },
    [
      isOnboardingStrategiesEnabled,
      workspace,
      selectedPlan?.key,
      isActive,
      closeAllModals,
      removeSettingsModalQueryParam,
      queryClient,
      canAutoReloadCredits,
      updateAutoReloadCredits,
      tierUsageStore,
      creditUsageStore,
      history,
    ],
  )

  const handleDowngradePlanForStrategies = useCallback(
    (overridePriceId?: string, overridePaymentMethodId?: string) => {
      const effectivePriceId = overridePriceId || chosenPriceId
      const effectivePaymentMethodId = overridePaymentMethodId !== undefined ? overridePaymentMethodId : paymentMethodId

      if (!effectivePriceId || !workspace) return

      const dismiss = showToastMessage('Processing plan change...', 'loading', undefined, 0)
      setIsProcessing(true)

      downgradeBrandPlan(
        {
          payment_method_id: effectivePaymentMethodId,
          price_id: effectivePriceId,
          promotion_code: promotionCode,
        },
        {
          onSuccess: async (brandPlan) => {
            await onDowngradeBrandPlanSuccess(brandPlan, effectivePriceId, dismiss)
          },
          onError: () => {
            dismiss()
            setIsProcessing(false)
            showToastMessage(
              'An error occurred during downgrade. Try again. Contact support if the issue persists.',
              'error',
            )
          },
        },
      )
    },
    [chosenPriceId, paymentMethodId, promotionCode, workspace, downgradeBrandPlan, onDowngradeBrandPlanSuccess],
  )

  const handleDowngradePlan = useCallback(
    (values?: FormValues, overridePriceId?: string, overridePaymentMethodId?: string) => {
      const channelData = values || savedChannelcounts
      const effectivePriceId = overridePriceId || chosenPriceId
      const effectivePaymentMethodId = overridePaymentMethodId !== undefined ? overridePaymentMethodId : paymentMethodId

      if (!channelData || !workspace) return

      const payload = buildLimitedChannels(channelData)

      if (selectedPlan?.key === PaymentPlanKey.Free) {
        WorkspaceClient.updateFutureFreemiumBrandPlanContents(workspace.id, payload)
      }

      const dismiss = showToastMessage('Processing plan change...', 'loading', undefined, 0)
      setIsProcessing(true)

      downgradeBrandPlan(
        {
          payment_method_id: effectivePaymentMethodId,
          price_id: effectivePriceId,
          promotion_code: promotionCode,
          strategy: { type: 'EvergreenStrategy' },
          ...payload,
        },
        {
          onSuccess: async (brandPlan) => {
            await onDowngradeBrandPlanSuccess(brandPlan, effectivePriceId, dismiss)
          },
          onError: () => {
            dismiss()
            setIsProcessing(false)
            showToastMessage(
              'An error occurred during downgrade. Try again. Contact support if the issue persists.',
              'error',
            )
          },
        },
      )
    },
    [
      savedChannelcounts,
      chosenPriceId,
      paymentMethodId,
      promotionCode,
      workspace,
      selectedPlan?.key,
      downgradeBrandPlan,
      onDowngradeBrandPlanSuccess,
    ],
  )

  const toggleAutoReloadCredits = () => {
    setShouldAutoReloadCredits(!shouldAutoReloadCredits)
  }

  if (!currentSubscription) return null

  // Render campaign_questions and channels outside Modal.Root to match onboarding styling
  if (step === 'campaign_questions') {
    return (
      <CampaignQuestions
        campaignQuestions={campaignQuestions}
        setCampaignQuestions={setCampaignQuestions}
        onNext={() => {
          setStep('channels')
        }}
        onBack={() => {
          setCampaignQuestions({ goals: '', keyDates: '' })
          handleBack()
        }}
        brandPlanId={brandPlan?.id}
        brandKitId={brandPlan?.brand_kit?.id || brandProfile?.brand_kit?.id}
      />
    )
  }

  if (step === 'channels' && brandPlan) {
    return (
      <Channels
        onSubmit={handleUpgradeBrandPlan}
        onBack={handleBack}
        brandPlan={brandPlan}
        isUpgradingBrandPlan={isUpgradingBrandPlan}
        externalIntegrations={externalIntegrationsData?.external_integrations}
        brandKitId={brandPlan.brand_kit?.id}
      />
    )
  }

  if (step === 'upgradeLoading' && brandPlan) {
    return (
      <UpgradeLoadingScreen
        brandPlanId={brandPlan.id}
        campaignData={{
          growth_goals_input: campaignQuestions.growth_goals_input || campaignQuestions.goals || '',
          key_dates_input: campaignQuestions.key_dates_input || campaignQuestions.keyDates || '',
        }}
        onComplete={() => {
          closeAllModals()
          removeSettingsModalQueryParam()
          queryClient.invalidateQueries(['can_create_additional_workspace'])
          if (workspace) {
            history.push(generatePath(CAMPAIGNS_PATH, { workspaceId: workspace.id }))
          }
        }}
        onError={() => {
          showToastMessage('Campaign generation failed. Please try again.', 'error')
          closeAllModals()
          removeSettingsModalQueryParam()
        }}
      />
    )
  }

  const showDowngradeConfirmation = (
    currentSubscription: FetchSubscriptionResponse | null,
    selectedPlan: AutopilotPlan | null,
    workspace?: Workspace,
  ) => {
    if (!currentSubscription || !selectedPlan || workspace?.is_ap_freemium) return false

    if (
      currentSubscription.plan === PaymentPlanKey.VirtualMarketer &&
      planOrder[currentSubscription.plan] === planOrder[selectedPlan.key]
    ) {
      return true
    }

    return planOrder[currentSubscription.plan] > planOrder[selectedPlan.key]
  }

  return (
    <Modal.Root onPressOutside={closeModal} bodyClassName={Styles.body}>
      {step === 'cta' && (
        <Cta
          onCancel={closeModal}
          onConfirm={() => setStep('plans')}
          isTrialing={isTrialing}
          onUpgradeToGrowth={() => {
            const growthPlan = tierUsageStore.autopilotPlans.find(
              (plan) => plan.key === PaymentPlanKey.BlazeAutopilotGrowth,
            )

            if (!growthPlan) return

            setSelectedPlan(growthPlan)
            setStep('selected')
          }}
        />
      )}
      {step === 'plans' && (
        <Plans
          onBack={handleBack}
          onCancelSubscription={handleCancelSubscription}
          onSelectPlan={(plan) => {
            if (currentSubscription.cancel_at && plan?.key === currentSubscription.plan && isActive) {
              // User is choosing to not cancel their subscription
              updateSubscription(
                {
                  paymentMethodId: undefined,
                  priceId: currentSubscription.prices?.[0]?.id,
                  reactivate,
                },
                {
                  onSuccess: () => {
                    const autoReloadLimit = canAutoReloadCredits ? 500 : null
                    updateAutoReloadCredits.mutate({ autoReloadCredits: canAutoReloadCredits, autoReloadLimit })
                  },
                },
              )
              return
            }

            setSelectedPlan(plan)

            if (isTrialing && plan && plan?.key !== currentSubscription.plan && plan?.key !== PaymentPlanKey.Free) {
              // User is choosing to trial another plan — disable auto reload for trial switches
              setShouldAutoReloadCredits(false)
              if (showDowngradeConfirmation(currentSubscription, plan, workspace)) {
                setStep('downgrade_confirmation')
              } else {
                // User is upgrading so this method should show them the campaign questions or activate the existing brand plan
                savePaymentInformation({
                  paymentMethodId: undefined,
                  chosenPriceId: plan.prices?.[0]?.id,
                  selectedPlanOverride: plan,
                })
              }
              return
            }

            if (showDowngradeConfirmation(currentSubscription, plan, workspace)) {
              setStep('downgrade_confirmation')
            } else {
              setStep('selected')
            }
          }}
          isProcessing={isProcessing || isUpdating}
        />
      )}
      {(step === 'selected' || step === 'checkout') && selectedPlan && (
        <Form
          onBack={handleBack}
          onChangePlan={() => {
            setSelectedPlan(null)
            setStep('plans')
          }}
          onStartPlan={savePaymentInformation}
          selectedPlan={selectedPlan}
          currentPrice={currentSubscription?.prices?.[0]}
          initialSelectedPrice={initialPlanAndPrice?.price}
          shouldAutoReloadCredits={canAutoReloadCredits}
          toggleAutoReloadCredits={toggleAutoReloadCredits}
          initialIsCheckoutStep={step === 'checkout'}
        />
      )}
      {step === 'downgrade_confirmation' && selectedPlan && (
        <DowngradeConfirmation
          onBack={handleBack}
          onConfirm={() => {
            if (selectedPlan.key === PaymentPlanKey.BlazeAutopilotStarter && !isTrialing) {
              setStep('selected')
            } else {
              openChannelSelectModal(selectedPlan.key, selectedPlan.prices?.[0]?.id)
            }
          }}
          isStarterPlan={selectedPlan.key === PaymentPlanKey.BlazeAutopilotStarter}
          currentPlanName={nameMap[currentSubscription?.plan]}
          trialDaysLeft={currentSubscription?.trial_days_left || 0}
          isDisabled={isDowngradingBrandPlan}
        />
      )}
      {step === 'lose_members' && (
        <LoseMembers
          onBack={handleBack}
          onConfirm={() => (isOnboardingStrategiesEnabled ? handleDowngradePlanForStrategies() : handleDowngradePlan())}
          members={allMembers}
          isDowngradingBrandPlan={isDowngradingBrandPlan}
        />
      )}
    </Modal.Root>
  )
}
